import { Dropdown, Menu, Space, Table, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../config/api";
import { useMyContext } from "../../../context/MyContext";
import dayjs from "dayjs";
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import Loader from "../../shared/loader/loader";

const Tickets = () => {
  const { user, notifications, token } = useMyContext();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery(
    ["tickets", user?.uid],
    async () => {
      if (!token) {
        throw new Error("No authentication token available");
      }
      try {
        const res = await api.get(`/tickets/user/${user?.uid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Ensure data exists before sorting
        const sortedData = res.data ? res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) : [];

        return sortedData;
      } catch (error) {
        if (error?.response?.status === 401 && error?.response?.data?.error === "You are not authorized to make this request") {
          window.location.reload();
          return null;
        }
        console.error("Error fetching user tickets:", error);
        throw error;
      }
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!user?.uid && !!token,
    }
  );

  const handleChatClick = async (record) => {
    try {
      await api.put(`/notifications/ticket-chat/${user?.uid}`, {
        role: user?.role,
        refId: record?._id,
        updateAllNotifications: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/tickets/${record?._id}`);
    } catch (error) {
      console.error("Error updating notification:", error);
    }
  };

  if (error) {
    return <div>Error loading tickets: {error.message}</div>;
  }

  const getStatusClasses = (status) => {
    switch (status) {
      case true:
        return 'bg-green-600 ';
      case false:
        return 'bg-red-700';
      default:
        return 'bg-gray-800';
    }
  }
  const getPriorityClasses = (priority) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return 'bg-violet-800 ';
      case 'high':
        return 'bg-red-700';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-sky-500';
      default:
        return 'bg-gray-800';
    }
  }

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (_, record) => dayjs(record?.createdAt).format("DD MMM YYYY"),
    },
    {
      title: "Quote / Order No.",
      dataIndex: "quoteNumber",
      render: (_, record) => (record?.quoteNumber || record?.orderNumber || "-"),
    },
    {
      title: "Type",
      dataIndex: "type",
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "left",
      render: (_, record) => (
        <div className="flex items-center">
          <span className={`rounded text-white px-2 py-1 font-medium inline-block ${getStatusClasses(record?.status)}`}>
            {record?.status ? "Open" : "Closed"}
          </span>
        </div>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      align: "left",
      render: (_, record) => (
        <div className="flex items-center">
          <span className={`rounded text-white px-2 py-1 font-medium inline-block ${getPriorityClasses(record?.priority)}`}>
            {record?.priority}
          </span>
        </div>
      ),
    },
    {
      title: "Actions",
      render: (_, record, index) => (
        <Dropdown
          placement="bottomRight"
          overlay={
            <Menu>
              <Menu.Item
                onClick={() => handleChatClick(record)}
                key="view ticket"
              >
                <Space>Chat</Space>
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Space className="relative">
            <button className="border relative p-1 rounded-md border-[#053F92]">
              <PiDotsThreeVerticalBold size={20} />
              {notifications?.find(
                (e) => e?.refId === record?._id && e?.read === false
              ) ? (
                <div className="w-2 h-2 absolute -top-[2px] right-0 bg-red-600 rounded-full"></div>
              ) : null}
            </button>
          </Space>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="flex flex-col p-5 bg-cus-gray h-full">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {data?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No tickets found
            </div>
          ) : (
            <Table columns={columns} dataSource={data} loading={isLoading} />
          )}
        </>
      )}
    </div>
  );
};

export default Tickets;
